/**
 * Lists Check-In Sessions of type 'in_store_return' linked to the work item
 * being viewed. Rendered on the Ticket Detail page underneath the main panel.
 * Hidden when the list is empty so it doesn't clutter jobs that never had a
 * return.
 */

import React, { useEffect, useState } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import type { CheckInReturnSummary } from '@techgitter/contracts';

import { fetchReturnsForWorkItem } from '../../services/checkInApi';
import {
  lifecycleLabel,
  lifecycleChipColor,
} from './sessionDetail/sessionDetailHelpers';
import { Chip, toneFromMuiColor } from '../chip';
import {
  DataTable,
  DataTableHeadCell,
  DataTableRow,
  DataTableCell,
} from '../table';
import { CARD_SX } from '../../styles/designSystem';

export interface JobReturnsPanelProps {
  workItemId: string;
}

export const JobReturnsPanel: React.FC<JobReturnsPanelProps> = ({ workItemId }) => {
  const [items, setItems] = useState<CheckInReturnSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!workItemId) {
      setItems([]);
      setLoading(false);
      return;
    }
    const controller = new AbortController();
    setLoading(true);
    setError(null);
    fetchReturnsForWorkItem(workItemId, controller.signal)
      .then(res => setItems(res))
      .catch(err => {
        if (controller.signal.aborted) return;
        console.warn('[JobReturnsPanel] failed to load returns:', err);
        setError('Failed to load returns.');
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [workItemId]);

  if (loading) {
    return (
      <Box sx={{ ...CARD_SX, p: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
        <CircularProgress size={16} />
        <Typography sx={{ fontSize: 13, color: '#888' }}>Loading returns…</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ ...CARD_SX, p: 2 }}>
        <Typography sx={{ fontSize: 13, color: '#B03A2E' }}>{error}</Typography>
      </Box>
    );
  }

  if (items.length === 0) {
    return null;
  }

  return (
    <Box sx={{ ...CARD_SX, overflow: 'hidden' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 2, pb: 1.5 }}>
        <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#1a1a1a' }}>
          Returns
        </Typography>
        <Chip>{items.length}</Chip>
      </Box>
      <DataTable>
        <thead>
          <tr>
            <DataTableHeadCell>Session</DataTableHeadCell>
            <DataTableHeadCell>Status</DataTableHeadCell>
            <DataTableHeadCell>RS Ticket</DataTableHeadCell>
            <DataTableHeadCell>Created</DataTableHeadCell>
            <DataTableHeadCell>By</DataTableHeadCell>
          </tr>
        </thead>
        <tbody>
          {items.map(r => (
            <DataTableRow key={r.sessionId}>
              <DataTableCell>{r.sessionNumber ?? r.sessionId}</DataTableCell>
              <DataTableCell>
                <Chip tone={toneFromMuiColor(lifecycleChipColor(r.lifecycleStatus))}>
                  {lifecycleLabel(r.lifecycleStatus)}
                </Chip>
              </DataTableCell>
              <DataTableCell>
                {r.ticketNumber
                  ? `#${r.ticketNumber}`
                  : r.repairShoprTicketId
                    ? `#${r.repairShoprTicketId}`
                    : '—'}
              </DataTableCell>
              <DataTableCell>
                {r.createdAt
                  ? new Date(r.createdAt).toLocaleDateString('en-AU', {
                      day: '2-digit', month: 'short', year: 'numeric',
                    })
                  : '—'}
              </DataTableCell>
              <DataTableCell>{r.createdBy || '—'}</DataTableCell>
            </DataTableRow>
          ))}
        </tbody>
      </DataTable>
    </Box>
  );
};
